import { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  lti1p1Variables,
  lti1p3Variables,
  inputFieldsLTI1p1,
  inputFieldsLTI1p3
} from 'pages/integrations/containers/LtiIntegrationContainer/constants';
import { ltiActions } from 'pages/integrations/containers/LtiIntegrationContainer/actions';

const LtiConfigurationForm = ({ dispatch, hideModal, isFormOpen, editMode, configuration }) => {
  const initialInputState = {
    name: '',
    description: '',
    // LTI 1.1 fields
    clientKey: '',
    clientSecret: '',
    ltiUrl: '',
    // LTI 1.3 fields
    toolLaunchUrl: '',
    loginUrl: '',
    publicKey: '',
    keysetUrl: '',
    privateKey: '',
    redirectUris: '',
    enableNrps: false,
    enableDeepLinking: false,
    deepLinkingLaunchUrl: '',
    agsMode: 'declarative'
  };
  const [selectedVersion, setSelectedVersion] = useState('');
  const [hiddenField, sethiddenField] = useState('');

  const [inputs, setInputs] = useState(initialInputState);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editMode) {
      setSelectedVersion(configuration.version);
      updateInputs();
    }
  }, [editMode]);

  useEffect(() => {
    if (inputs.publicKey.length > 0) {
      sethiddenField('keysetUrl');
    } else if (inputs.keysetUrl.length > 0) {
      sethiddenField('publicKey');
    } else {
      sethiddenField('');
    }
  }, [inputs]);

  const hideInputField = (name) => {
    if (hiddenField === name) {
      return true;
    }
    return false;
  };

  const updateInputs = () => {
    const mappingDict = configuration.version === 'lti_1p1' ? lti1p1Variables : lti1p3Variables;
    const inputsDict = {};
    for (const key in mappingDict) {
      inputsDict[key] = configuration[mappingDict[key]];
    }
    delete inputsDict.version;

    setInputs((prevInputs) => ({
      ...prevInputs,
      ...inputsDict
    }));
  };

  const isValidRSAPrivateKey = (privateKey) => {
    const trimmedKey = privateKey.trim();
    // Check if it looks like a PEM formatted private key
    const pemHeaderRegex = /^-----BEGIN (RSA )?PRIVATE KEY-----/m;
    const pemFooterRegex = /-----END (RSA )?PRIVATE KEY-----$/m;

    if (!pemHeaderRegex.test(trimmedKey) || !pemFooterRegex.test(trimmedKey)) {
      return false;
    }

    // Check if it has the basic structure (header, content, footer)
    const lines = trimmedKey
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);
    if (lines.length < 3) return false;

    // Check if middle lines contain base64-like content (allow dots for truncation in examples)
    const base64Regex = /^[A-Za-z0-9+/=.]*$/;
    for (let i = 1; i < lines.length - 1; i++) {
      if (lines[i] && !base64Regex.test(lines[i])) {
        return false;
      }
    }

    return true;
  };

  const isValidUrl = (value) => {
    if (!value) return false;
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  // Redirect URIs, when provided, must be a JSON array of valid http(s) URLs.
  // Returns an error message string, or '' when valid.
  const validateRedirectUris = (value) => {
    let parsed;
    try {
      parsed = JSON.parse(value.trim());
    } catch (error) {
      return 'Redirect URIs must be a valid JSON array, e.g. ["https://example.com/launch"].';
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return 'Redirect URIs must be a non-empty JSON array, e.g. ["https://example.com/launch"].';
    }
    if (!parsed.every((uri) => typeof uri === 'string' && isValidUrl(uri))) {
      return 'Each redirect URI must be a valid http:// or https:// URL.';
    }
    return '';
  };

  const saveConfiguration = () => {
    // Validate required fields
    if (!inputs.name || !inputs.name.trim()) {
      alert('Configuration name is required.');
      return;
    }

    // Validate LTI 1.1 URL when provided.
    if (selectedVersion === 'lti_1p1') {
      if (inputs.ltiUrl && inputs.ltiUrl.trim() && !isValidUrl(inputs.ltiUrl)) {
        alert('Please enter a valid LTI URL (must start with http:// or https://).');
        return;
      }
    }

    if (selectedVersion === 'lti_1p3') {
      // Required launch/login URLs must be well-formed.
      if (!isValidUrl(inputs.toolLaunchUrl)) {
        alert('Please enter a valid Tool Launch URL (must start with http:// or https://).');
        return;
      }
      if (!isValidUrl(inputs.loginUrl)) {
        alert('Please enter a valid Tool Initiate Login URL (must start with http:// or https://).');
        return;
      }

      // Optional keyset URL, validate only when provided.
      if (inputs.keysetUrl && inputs.keysetUrl.trim() && !isValidUrl(inputs.keysetUrl)) {
        alert('Please enter a valid Tool Keyset URL (must start with http:// or https://).');
        return;
      }

      // Optional redirect URIs must be a JSON array of valid URLs.
      if (inputs.redirectUris && inputs.redirectUris.trim()) {
        const redirectUrisError = validateRedirectUris(inputs.redirectUris);
        if (redirectUrisError) {
          alert(redirectUrisError);
          return;
        }
      }

      // Validate private key format (required for LTI 1.3).
      if (!isValidRSAPrivateKey(inputs.privateKey)) {
        alert(
          'Invalid RSA private key format. Please provide a valid PEM-formatted RSA private key or leave blank for auto-generation.'
        );
        return;
      }

      // Deep linking, when enabled, requires a valid launch URL.
      if (inputs.enableDeepLinking) {
        if (!inputs.deepLinkingLaunchUrl || !inputs.deepLinkingLaunchUrl.trim()) {
          alert('Deep Linking Launch URL is required when deep linking is enabled.');
          return;
        }
        if (!isValidUrl(inputs.deepLinkingLaunchUrl)) {
          alert('Please enter a valid Deep Linking Launch URL (must start with http:// or https://).');
          return;
        }
      }
    }

    setIsLoading(true);
    const submissionData = { ...inputs };
    submissionData.version = selectedVersion;
    // The checkbox is authoritative: clear the URL when deep linking is disabled.
    if (!submissionData.enableDeepLinking) {
      submissionData.deepLinkingLaunchUrl = '';
    }

    if (editMode) {
      dispatch(
        ltiActions.patchLtiConfigurations(configuration.id, { ...submissionData, editMode: editMode }),
        setIsLoading
      );
    } else {
      dispatch(ltiActions.createLtiConfigurations({ ...submissionData, editMode: editMode }), setIsLoading);
    }
    setInputs(initialInputState);
    hideModal();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInputs((prevInputs) => ({
      ...prevInputs,
      [name]: value
    }));
  };

  const fieldValue = (value, name) => {
    // Return the actual value, don't modify it for display
    return value || '';
  };

  const isFieldDisabled = (field) => {
    // Don't disable course key field in edit mode - allow users to modify it
    if (field.readOnly) return true;
    return false;
  };

  const isFieldRequired = (field) => {
    return field.required || false;
  };

  const isFieldHidden = (field) => {
    if (hideInputField(field.name)) return true;
    // The Deep Linking Launch URL only appears once deep linking is enabled.
    if (field.name === 'deepLinkingLaunchUrl' && !inputs.enableDeepLinking) return true;
    return false;
  };

  const getInputFields = () => {
    if (selectedVersion === 'lti_1p1') return inputFieldsLTI1p1;
    else if (selectedVersion === 'lti_1p3') return inputFieldsLTI1p3;
    else return [];
  };

  const areAllRequiredFieldsFilled = () => {
    if (!selectedVersion) return false;

    const inputFields = getInputFields();

    // Check all required fields with special handling for publicKey/keysetUrl
    return inputFields.every((field) => {
      if (field.required) {
        const value = inputs[field.name];

        // Special case for publicKey: it's required but can be satisfied by keysetUrl instead
        if (field.name === 'publicKey' && selectedVersion === 'lti_1p3') {
          const hasPublicKey = Boolean(inputs.publicKey?.trim());
          const hasKeysetUrl = Boolean(inputs.keysetUrl?.trim());
          if (!hasPublicKey && !hasKeysetUrl) {
            return false;
          }
          return true;
        }

        // Normal required field validation
        if (!value || (typeof value === 'string' && !value.trim())) {
          return false;
        }
      }
      return true;
    });
  };

  return (
    <Modal onHide={hideModal} show={isFormOpen} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add LTI Configuration</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Select value={selectedVersion} onChange={(e) => setSelectedVersion(e.target.value)} disabled={editMode}>
            <option>Select LTI version</option>
            <option value="lti_1p1">LTI Version (1.1) </option>
            <option value="lti_1p3">LTI Version (1.3)</option>
          </Form.Select>
        </Form.Group>
        {getInputFields().map((field, index) => (
          <Form.Group className="mb-3" key={index} hidden={isFieldHidden(field)}>
            <Form.Label hidden={isFieldHidden(field)}>
              {field.label}
              {isFieldRequired(field) && <span className="text-danger"> *</span>}
            </Form.Label>

            {field.type === 'checkbox' ? (
              <Form.Check
                type="checkbox"
                name={field.name}
                checked={inputs[field.name] || false}
                onChange={(e) => handleInputChange({ target: { name: field.name, value: e.target.checked } })}
                hidden={isFieldHidden(field)}
                disabled={isFieldDisabled(field)}
                label={field.label}
              />
            ) : field.type === 'select' ? (
              <Form.Select
                name={field.name}
                value={inputs[field.name] || ''}
                onChange={handleInputChange}
                hidden={isFieldHidden(field)}
                disabled={isFieldDisabled(field)}
                required={isFieldRequired(field)}
              >
                <option value="">Select an option</option>
                {field.options?.map((option, optIndex) => (
                  <option key={optIndex} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            ) : field.type === 'textarea' ? (
              <Form.Control
                as="textarea"
                rows={3}
                name={field.name}
                value={inputs[field.name] || ''}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                hidden={isFieldHidden(field)}
                disabled={isFieldDisabled(field)}
                required={isFieldRequired(field)}
              />
            ) : (
              <Form.Control
                type={field.type}
                name={field.name}
                value={fieldValue(inputs[field.name], field.name)}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                hidden={isFieldHidden(field)}
                disabled={isFieldDisabled(field)}
                required={isFieldRequired(field)}
              />
            )}

            <Form.Text hidden={isFieldHidden(field)} className="text-muted">
              {field.helptext}
            </Form.Text>
          </Form.Group>
        ))}

        {selectedVersion && (
          <Modal.Footer>
            <Button
              style={{ paddingLeft: '30px', paddingRight: '30px' }}
              onClick={saveConfiguration}
              disabled={!areAllRequiredFieldsFilled() || isLoading}
            >
              {isLoading && (
                <span
                  className="spinner-border spinner-border-sm pr-2"
                  style={{ marginRight: '8px' }}
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              Save
            </Button>
          </Modal.Footer>
        )}
      </Modal.Body>
    </Modal>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(ltiActions, dispatch)
  };
}
LtiConfigurationForm.defaultProps = {
  version: '',
  editMode: false
};
export default connect(mapDispatchToProps)(LtiConfigurationForm);
